/**
 * Notification utility functions
 * 
 * Helpers for working with activity events (notifications) including URL building and metadata extraction
 */

import type { ActivityEvent, Notification } from "@/api/generated/client";

/**
 * Union type for event-like objects (ActivityEvent or Notification)
 */
type EventLike = ActivityEvent | Notification;

/**
 * Type guard to check if metadata is a valid record
 */
export function isMetadataRecord(metadata: unknown): metadata is Record<string, unknown> {
    return typeof metadata === 'object' && metadata !== null && !Array.isArray(metadata);
}

/**
 * Safely extract claim ID from metadata
 */
export function getClaimIdFromMetadata(metadata: unknown): number | null {
    if (!isMetadataRecord(metadata)) {
        return null;
    }
    
    const claimId = metadata.claimId;
    return typeof claimId === 'number' ? claimId : null;
}

/**
 * Safely extract URL from metadata
 */
function getUrlFromMetadata(metadata: unknown): string | null {
    if (!isMetadataRecord(metadata)) {
        return null;
    }
    
    const url = metadata.url;
    return typeof url === 'string' ? url : null;
}

/**
 * Safely extract entity type from metadata
 */
function getEntityTypeFromMetadata(metadata: unknown): string | null {
    if (!isMetadataRecord(metadata)) {
        return null;
    }
    
    const entityType = metadata.entityType;
    return typeof entityType === 'string' ? entityType : null;
}

/**
 * Safely extract entity ID from metadata
 */
function getEntityIdFromMetadata(metadata: unknown): string | null {
    if (!isMetadataRecord(metadata)) {
        return null;
    }
    
    const entityId = metadata.entityId;
    return typeof entityId === 'string' || typeof entityId === 'number' ? String(entityId) : null;
}

/**
 * Safely extract resource type from metadata
 */
function getResourceTypeFromMetadata(metadata: unknown): string | null {
    if (!isMetadataRecord(metadata)) {
        return null;
    }
    
    const resourceType = metadata.resourceType;
    return typeof resourceType === 'string' ? resourceType : null;
}

/**
 * Safely extract resource ID from metadata
 */
function getResourceIdFromMetadata(metadata: unknown): string | null {
    if (!isMetadataRecord(metadata)) {
        return null;
    }
    
    const resourceId = metadata.resourceId;
    return typeof resourceId === 'string' || typeof resourceId === 'number' ? String(resourceId) : null;
}

/**
 * Get navigation URL from activity event
 * 
 * Builds a URL based on the entity type and claim ID.
 * For claim-scoped events, navigates to the claim detail page.
 * 
 * @param event - The activity event object
 * @returns URL string to navigate to, or null if cannot be determined
 * 
 * @example
 * ```ts
 * const url = getNotificationUrl(event);
 * if (url) navigate(url);
 * ```
 */
export function getNotificationUrl(event: EventLike): string | null {
    // Check for direct url property (Notification type)
    if ('url' in event && event.url) {
        return event.url;
    }
    
    // Check metadata for URL (ActivityEvent type)
    const url = getUrlFromMetadata(event.metadata);
    if (url) {
        return url;
    }
    
    // Check top-level claimId property (ActivityEvent type)
    if ('claimId' in event && event.claimId) {
        return `/claims/${event.claimId}`;
    }
    
    // Check metadata for claimId (fallback)
    const claimIdFromMeta = getClaimIdFromMetadata(event.metadata);
    if (claimIdFromMeta) {
        return `/claims/${claimIdFromMeta}`;
    }
    
    // For entity-scoped events, build URL based on entity type
    const entityType = getEntityTypeFromMetadata(event.metadata);
    const entityId = getEntityIdFromMetadata(event.metadata);
    
    if (entityType && entityId) {
        switch (entityType) {
            case 'claim':
                return `/claims/${entityId}`;
            case 'comparison':
            case 'supplement':
            case 'document':
                // These need a claimId to navigate, already checked above
                break;
        }
    }
    
    return null;
}

/**
 * Extract task titles from activity event metadata
 * 
 * @param event - The activity event object
 * @returns Array of task titles, or empty array if none found
 */
export function getNotificationTaskTitles(event: EventLike): string[] {
    if (!isMetadataRecord(event.metadata)) {
        return [];
    }
    
    const taskTitles = event.metadata.taskTitles;
    return Array.isArray(taskTitles) && taskTitles.every(item => typeof item === 'string')
        ? taskTitles
        : [];
}

/**
 * Check if activity event has task details to display
 * 
 * @param event - The activity event object
 * @returns True if event has task titles in metadata
 */
export function hasNotificationTaskDetails(event: EventLike): boolean {
    const taskTitles = getNotificationTaskTitles(event);
    return taskTitles.length > 0;
}

/**
 * Extract claim number from activity event metadata
 * 
 * @param event - The activity event object
 * @returns Claim number string, or null if not found
 */
export function getNotificationClaimNumber(event: EventLike): string | null {
    if (!isMetadataRecord(event.metadata)) {
        return null;
    }
    
    const claimNumber = event.metadata.claimNumber;
    return typeof claimNumber === 'string' ? claimNumber : null;
}

/**
 * Extract claim ID from activity event
 * 
 * @param event - The activity event object
 * @returns Claim ID number, or null if not found
 */
export function getNotificationClaimId(event: EventLike): number | null {
    // Check direct claimId property (ActivityEvent type)
    if ('claimId' in event && typeof event.claimId === 'number') {
        return event.claimId;
    }

    // Check metadata for claimId
    const claimId = getClaimIdFromMetadata(event.metadata);
    if (claimId) {
        return claimId;
    }
    
    // Check direct resourceType/resourceId properties (Notification type)
    if ('resourceType' in event && 'resourceId' in event) {
        if (event.resourceType === 'claim' && event.resourceId) {
            const parsedId = parseInt(event.resourceId, 10);
            return isNaN(parsedId) ? null : parsedId;
        }
    }
    
    // Check metadata for resourceType/resourceId
    const resourceType = getResourceTypeFromMetadata(event.metadata);
    const resourceId = getResourceIdFromMetadata(event.metadata);
    if (resourceType === 'claim' && resourceId) {
        const parsedId = parseInt(resourceId, 10);
        return isNaN(parsedId) ? null : parsedId;
    }
    
    // If entityType is claim, use entityId
    const entityType = getEntityTypeFromMetadata(event.metadata);
    const entityId = getEntityIdFromMetadata(event.metadata);
    if (entityType === 'claim' && entityId) {
        const parsedId = parseInt(entityId, 10);
        return isNaN(parsedId) ? null : parsedId;
    }
    
    return null;
}
